/**
 * Timer Component
 *
 * Per-step timer for cooking mode with start/pause functionality.
 * Features:
 * - Countdown timer display
 * - Start/Pause/Reset controls
 * - Visual and audio alerts when time expires
 * - Persistent state during cooking session
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './cooking.css';

interface TimerProps {
  stepIndex: number;
  defaultDuration?: number; // Duration in minutes
}

export function Timer({ stepIndex, defaultDuration = 10 }: TimerProps) {
  const { t } = useTranslation('cooking');
  const [duration, setDuration] = useState<number>(defaultDuration * 60); // Convert to seconds
  const [remainingTime, setRemainingTime] = useState<number>(duration);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const intervalRef = useRef<number | null>(null);

  // Clear interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Timer countdown logic
  useEffect(() => {
    if (isRunning && remainingTime > 0) {
      intervalRef.current = window.setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            setIsExpired(true);
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
            }
            // Play audio alert
            playAlert();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, remainingTime]);

  // Reset timer when step changes
  useEffect(() => {
    handleReset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex]);

  const playAlert = () => {
    // Try to play a simple beep using Web Audio API
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.error('Failed to play alert sound:', error);
    }
  };

  const handleStartPause = () => {
    if (isExpired) {
      handleReset();
    } else {
      setIsRunning(!isRunning);
    }
  };

  const handleReset = () => {
    setIsRunning(false);
    setIsExpired(false);
    setRemainingTime(duration);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const handleDurationChange = (newDuration: number) => {
    const seconds = newDuration * 60;
    setDuration(seconds);
    setRemainingTime(seconds);
    setIsRunning(false);
    setIsExpired(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? ((duration - remainingTime) / duration) * 100 : 0;

  return (
    <div className="timer-container">
      <div className="timer-header">
        <h3 className="timer-title">{'⏱ '}{t('stepTimer')}</h3>
      </div>

      {/* Timer Display */}
      <div className={`timer-display ${isExpired ? 'timer-expired' : ''} ${isRunning ? 'timer-running' : ''}`}>
        <div className="timer-time" aria-live="polite" aria-atomic="true">
          {formatTime(remainingTime)}
        </div>
        {isExpired && (
          <div className="timer-expired-label" role="alert">
            {t('timesUp')}
          </div>
        )}
      </div>

      {/* Progress Ring */}
      <div className="timer-progress-ring">
        <svg width="120" height="120" viewBox="0 0 120 120">
          <circle
            cx="60"
            cy="60"
            r="54"
            fill="none"
            stroke="rgba(255, 255, 255, 0.1)"
            strokeWidth="8"
          />
          <circle
            cx="60"
            cy="60"
            r="54"
            fill="none"
            stroke={isExpired ? '#fca5a5' : '#646cff'}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${2 * Math.PI * 54}`}
            strokeDashoffset={`${2 * Math.PI * 54 * (1 - progress / 100)}`}
            transform="rotate(-90 60 60)"
            style={{ transition: 'stroke-dashoffset 0.3s linear' }}
          />
        </svg>
      </div>

      {/* Controls */}
      <div className="timer-controls">
        <button
          onClick={handleStartPause}
          className={`timer-button ${isRunning ? 'timer-button-pause' : 'timer-button-start'}`}
          aria-label={isRunning ? 'Pause timer' : isExpired ? 'Restart timer' : 'Start timer'}
        >
          {isExpired ? <>{'\u{1F504} '}{t('restart')}</> : isRunning ? <>{'⏸ '}{t('pause')}</> : <>{'▶ '}{t('start')}</>}
        </button>
        <button
          onClick={handleReset}
          className="timer-button timer-button-reset"
          disabled={!isRunning && remainingTime === duration}
          aria-label="Reset timer"
        >
          {'↻ '}{t('reset')}
        </button>
      </div>

      {/* Duration Presets */}
      <div className="timer-presets">
        <span className="timer-presets-label">{t('quickSet')}</span>
        <button
          onClick={() => handleDurationChange(5)}
          className={`timer-preset ${duration === 300 ? 'timer-preset-active' : ''}`}
          aria-label="Set timer to 5 minutes"
        >
          5m
        </button>
        <button
          onClick={() => handleDurationChange(10)}
          className={`timer-preset ${duration === 600 ? 'timer-preset-active' : ''}`}
          aria-label="Set timer to 10 minutes"
        >
          10m
        </button>
        <button
          onClick={() => handleDurationChange(15)}
          className={`timer-preset ${duration === 900 ? 'timer-preset-active' : ''}`}
          aria-label="Set timer to 15 minutes"
        >
          15m
        </button>
        <button
          onClick={() => handleDurationChange(20)}
          className={`timer-preset ${duration === 1200 ? 'timer-preset-active' : ''}`}
          aria-label="Set timer to 20 minutes"
        >
          20m
        </button>
      </div>
    </div>
  );
}

export default Timer;
